import { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Bullets, Buttons, FlexContainer, ModalContext, Spinner, Text } from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import { showModalAction } from 'Store/modules/app/actions';
import { routeStateSelector } from 'Store/modules/router/selectors';
import { useTranslation } from 'Hooks/useTranslation';
import {
  useFetchGetPreviousYearsPercentage,
  useFetchGetResultPreviousYear,
} from 'Containers/PreviousYears/hooks';
import { parsePriceFormat } from 'Containers/ResultXml/components/PaymentLaterXml/utils';
import { EfirmaInfo } from 'Components/Modal/components';
import { EFIRMA_PREVIOUS_YEAR } from 'Constants/modals';
import { setBodyOverflow } from 'Styles/utils/utils';
import { plansLogos } from 'Constants/plans';
import * as Styled from './styles';

const PreviousYearsEfirma = (): JSX.Element => {
  const { showModal } = useContext(ModalContext);
  const { mutate, isLoading, data: price } = useFetchGetPreviousYearsPercentage();
  const result = useSelector(routeStateSelector);
  const data = useFetchGetResultPreviousYear();
  const { t } = useTranslation(locizeNS.PY);
  const dispatch = useDispatch();

  useEffect(() => {
    if (data?.amountPending || result?.amount) {
      const amount = data?.amountPending || result?.amount;
      mutate({ amount: amount * 100 });
    }
  }, [data, result]);

  const hanldeShowModal = (): void => {
    showModal({
      comp: <EfirmaInfo isPayLater />,
    });
  };

  const handleClick = (): void => {
    setBodyOverflow();
    dispatch(
      showModalAction({
        options: {
          type: EFIRMA_PREVIOUS_YEAR,
        },
      })
    );
  };

  return (
    <Styled.ThreeColLayout>
      <Styled.TitleContainer type="fds_heading_bold_H4" color="dark_500" isDesktop>
        {t('PreviousYears.title')}
      </Styled.TitleContainer>
      <FlexContainer direction="column">
        <Styled.TitleContainer type="fds_heading_bold_H4" color="dark_500">
          {t('PreviousYears.title')}
        </Styled.TitleContainer>
        <Styled.Container align="center" direction="column" gap="dimension_24">
          {isLoading ? (
            <Spinner size="M" align="center" justify="center" />
          ) : (
            <>
              <Text type="headline" align="center" color="dark" size="XS_L_M">
                {t('PreviousYears.efirma.service.title')}
              </Text>
              <FlexContainer align="center" direction="column" gap="dimension_16">
                <Styled.ServiceTitle align="center" gap="dimension_04">
                  <img src={plansLogos.BASIC} alt="logo" />
                  <Text type="headline" align="center" size="L_S_S" color="dark">
                    {t('PreviousYears.title').toUpperCase()}
                  </Text>
                </Styled.ServiceTitle>
                <Text type="paragraphs" align="center" color="grey" size="M_M_S">
                  {t('PreviousYears.efirma.service.description')}
                </Text>
              </FlexContainer>
              <Styled.BulletsContainer fullWidth>
                <Bullets
                  icon={{ type: 'IconTickCleanSGreen', width: 16, height: 16 }}
                  spacing="M"
                  bullets={[
                    { text: t(`PreviousYears.efirma.service.info.devolution`) },
                    { text: t(`PreviousYears.efirma.service.info.risk`) },
                    { text: t(`PreviousYears.efirma.service.info.sat`) },
                  ]}
                />
              </Styled.BulletsContainer>
              <FlexContainer gap="dimension_48">
                <Text type="paragraphs" align="center" color="dark" size="M_XL_S">
                  {t('PreviousYears.efirma.service.cost')}
                </Text>
                {price && (
                  <Text type="paragraphs" align="center" color="dark" size="M_XL_S">
                    {parsePriceFormat(price.data.item.price)}
                  </Text>
                )}
              </FlexContainer>
              <FlexContainer direction="column" gap="dimension_16">
                <Buttons.Classic
                  label={t('PreviousYears.efirma.service.button')}
                  mode="primary"
                  size="m"
                  onClick={handleClick}
                />
                <Buttons.Tiny
                  label={t('PreviousYears.efirma.service.tiny')}
                  onClick={hanldeShowModal}
                />
              </FlexContainer>
            </>
          )}
        </Styled.Container>
      </FlexContainer>
    </Styled.ThreeColLayout>
  );
};

export default PreviousYearsEfirma;
